import { useMemo } from "react";
import type { ExtraType } from "../../../../common-deprecated/types/CommonTypes";
import { useCarConfigSelector } from "../redux/store";
import type { ImageCarConfigType, ImageCarType } from "../../../../common-deprecated/settings/utils/ccisSettingUtils";
import CarDataConsolidator from "../../../shared-logic/utils/classes/CarDataConsolidator";

export const useCarIds = (): ImageCarType => {
    const car = useCarConfigSelector((state) => state.car);
    return useMemo(
        () => ({
            modelId: car.model.id,
            carId: car.id,
            gradeId: car.grade.id,
            bodyTypeId: car.body.id,
        }),
        [car.model.id, car.id, car.grade.id, car.body.id],
    );
};

const getWheelsShortId = <T extends { standard: boolean; shortId: string }>(wheel: T): string => {
    if (!wheel.standard) {
        return wheel.shortId;
    }
    return "";
};

const getExtraShortIds = (extras: ExtraType[], type: "option" | "accessory"): string[] => {
    return extras
        .filter((extra) => extra.type === type)
        .filter(
            (extra) =>
                (!extra.standard && !extra.selectedOptionId) || extra.parent || extra.isUpholstery || extra.isPackExtra,
        )
        .map((extra) => extra.shortId);
};

export const useCarConfigCCISNormalization = (): ImageCarConfigType => {
    const basket = useCarConfigSelector((state) => state.basket);
    const items = useCarConfigSelector((state) => state.items);
    const { colour, upholstery, wheel, winterWheel, packs } = basket;
    const extras = CarDataConsolidator.getAllRelevantBasketExtras(basket, items);
    const winterWheelTabActive = useCarConfigSelector((state) => state.carConfigNavigation.winterWheelTabActive);

    const toRenderWheel = winterWheel && winterWheelTabActive ? winterWheel : wheel;
    const wheelShortId = useMemo(() => getWheelsShortId(toRenderWheel), [toRenderWheel]);
    const accessoryIds = useMemo(() => getExtraShortIds(extras, "accessory"), [extras]);
    const optionIds = useMemo(() => getExtraShortIds(extras, "option"), [extras]);
    const packIds = useMemo(() => packs.map(({ shortId }) => shortId), [packs]);

    return useMemo<ImageCarConfigType>(() => {
        return {
            colorCode: colour.internalCode,
            accessoryIds:
                wheelShortId && toRenderWheel.type === "accessory" ? [wheelShortId, ...accessoryIds] : accessoryIds,
            packIds,
            optionIds: wheelShortId && toRenderWheel.type === "option" ? [wheelShortId, ...optionIds] : optionIds,
            upholsteryCode: upholstery.internalCode,
        };
    }, [
        colour.internalCode,
        wheelShortId,
        toRenderWheel.type,
        accessoryIds,
        packIds,
        optionIds,
        upholstery.internalCode,
    ]);
};
